/**
 * CloudUsagePanel - Suivi des services cloud payants (AWS Textract, Google Vision)
 * Affiche les statistiques d'utilisation, les coûts estimés et le suivi budgétaire
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import StatCard from './StatCard';

const CloudUsagePanel = ({ token, dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [budget, setBudget] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [available, setAvailable] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end
      });

      const [usageResponse, budgetResponse, recentResponse] = await Promise.all([
        fetch(`${API_ENDPOINTS.ANALYTICS_CLOUD_USAGE}?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.ANALYTICS_CLOUD_USAGE_BUDGET, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_ENDPOINTS.ANALYTICS_CLOUD_USAGE_RECENT}?limit=30`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const [usageData, budgetData, recentData] = await Promise.all([
        usageResponse.json(),
        budgetResponse.json(),
        recentResponse.json()
      ]);

      if (usageData.success && usageData.data.available) {
        setStats(usageData.data.stats || []);
        setDailyUsage(usageData.data.dailyUsage || []);
        setTopUsers(usageData.data.topUsers || []);
        setAvailable(true);
      } else {
        setAvailable(usageData.data?.available !== false);
      }

      if (budgetData.success && budgetData.data.available) {
        setBudget(budgetData.data);
      }

      if (recentData.success && recentData.data.available) {
        setRecentCalls(recentData.data.calls || []);
      }
    } catch (err) {
      console.error('Erreur chargement cloud usage:', err);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        Chargement des statistiques cloud...
      </div>
    );
  }

  if (!available) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#fff3cd',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <p style={{ fontSize: '16px', margin: 0 }}>
          La table cloud_service_usage n'est pas disponible.
        </p>
        <p style={{ fontSize: '14px', marginTop: '10px' }}>
          Exécutez la migration pour activer le suivi des services cloud.
        </p>
      </div>
    );
  }

  // Calculs des totaux
  const totalCalls = stats.reduce((sum, s) => sum + s.totalCalls, 0);
  const totalCost = stats.reduce((sum, s) => sum + s.totalCost, 0);
  const successRate = totalCalls > 0
    ? Math.round(stats.reduce((sum, s) => sum + s.successfulCalls, 0) / totalCalls * 100)
    : 0;

  // Grouper les stats par service
  const statsByService = stats.reduce((acc, s) => {
    if (!acc[s.service]) {
      acc[s.service] = { calls: 0, cost: 0, avgDuration: 0, count: 0 };
    }
    acc[s.service].calls += s.totalCalls;
    acc[s.service].cost += s.totalCost;
    acc[s.service].avgDuration += s.avgDurationMs * s.totalCalls;
    acc[s.service].count += s.totalCalls;
    return acc;
  }, {});

  // Calculer la moyenne pondérée des durées
  Object.keys(statsByService).forEach(service => {
    if (statsByService[service].count > 0) {
      statsByService[service].avgDuration = Math.round(
        statsByService[service].avgDuration / statsByService[service].count
      );
    }
  });

  const serviceLabels = {
    aws_textract: { name: 'AWS Textract', icon: '☁️', color: '#ff9900' },
    google_vision: { name: 'Google Vision', icon: '🔍', color: '#4285f4' },
    huggingface: { name: 'HuggingFace', icon: '🤗', color: '#ff6f00' }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'exceeded': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'moderate': return '#17a2b8';
      default: return '#28a745';
    }
  };

  const formatCurrency = (amount) => {
    // Micro-coûts HF : afficher en notation scientifique ou avec assez de décimales
    if (amount > 0 && amount < 0.0001) {
      return `$${amount.toFixed(6)}`;
    }
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Onglets */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        borderBottom: '2px solid #e0e0e0',
        paddingBottom: '10px'
      }}>
        {[
          { key: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
          { key: 'daily', label: 'Par jour', icon: '📅' },
          { key: 'users', label: 'Par utilisateur', icon: '👥' },
          { key: 'recent', label: 'Appels récents', icon: '📝' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              backgroundColor: activeTab === tab.key ? '#007bff' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.key ? '600' : '400',
              transition: 'all 0.2s'
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Vue d'ensemble */}
      {activeTab === 'overview' && (
        <>
          {/* Cards de stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <StatCard
              icon="☁️"
              title="Appels cloud"
              value={totalCalls}
              color="#007bff"
              subtitle={`${successRate}% de succès`}
            />
            <StatCard
              icon="💰"
              title="Coût estimé"
              value={formatCurrency(totalCost)}
              color="#28a745"
              subtitle="Période sélectionnée"
            />
            {budget && (
              <StatCard
                icon="📈"
                title="Budget MTD"
                value={`${budget.percentUsed}%`}
                color={getStatusColor(budget.status)}
                subtitle={`${formatCurrency(budget.totalCost)} / ${formatCurrency(budget.budget)}`}
              />
            )}
          </div>

          {/* Budget détaillé */}
          {budget && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#333' }}>
                Budget mensuel ({budget.month})
              </h3>
              <div style={{
                height: '24px',
                backgroundColor: '#e9ecef',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '12px'
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min(budget.percentUsed, 100)}%`,
                  backgroundColor: getStatusColor(budget.status),
                  borderRadius: '12px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#666'
              }}>
                <span>Utilisé: {formatCurrency(budget.totalCost)}</span>
                <span>Restant: {formatCurrency(budget.remaining)}</span>
                <span>Budget: {formatCurrency(budget.budget)}</span>
              </div>
            </div>
          )}

          {/* Stats par service */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#333' }}>
              Par service
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              {Object.entries(statsByService).map(([service, data]) => {
                const serviceInfo = serviceLabels[service] || { name: service, icon: '⚡', color: '#666' };
                return (
                  <div key={service} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${serviceInfo.color}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '24px' }}>{serviceInfo.icon}</span>
                      <div>
                        <div style={{ fontWeight: '600', color: '#333' }}>{serviceInfo.name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {data.calls} appels - moy. {data.avgDuration}ms
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: serviceInfo.color
                    }}>
                      {formatCurrency(data.cost)}
                    </div>
                  </div>
                );
              })}
              {Object.keys(statsByService).length === 0 && (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                  Aucun appel cloud sur cette période
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Par jour */}
      {activeTab === 'daily' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#333' }}>
            Utilisation par jour
          </h3>
          <div style={{ overflowX: 'auto' }}>
            {(() => {
              // Collecter dynamiquement tous les services présents dans les données
              const allServices = [...new Set(dailyUsage.flatMap(day => Object.keys(day.services)))];
              const colCount = 3 + allServices.length; // Date + services + Total + Coût
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                      <th style={thStyle}>Date</th>
                      {allServices.map(service => (
                        <th key={service} style={thStyle}>
                          {(serviceLabels[service] || {}).icon || '⚡'} {(serviceLabels[service] || {}).name || service}
                        </th>
                      ))}
                      <th style={thStyle}>Total appels</th>
                      <th style={thStyle}>Coût total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyUsage.map((day, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={tdStyle}>{day.date}</td>
                        {allServices.map(service => (
                          <td key={service} style={tdStyle}>
                            {day.services[service]?.calls || 0}
                            {day.services[service]?.cost > 0 && (
                              <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px' }}>
                                ({formatCurrency(day.services[service].cost)})
                              </span>
                            )}
                          </td>
                        ))}
                        <td style={tdStyle}>{day.totalCalls}</td>
                        <td style={{ ...tdStyle, fontWeight: '600', color: '#28a745' }}>
                          {formatCurrency(day.totalCost)}
                        </td>
                      </tr>
                    ))}
                    {dailyUsage.length === 0 && (
                      <tr>
                        <td colSpan={colCount} style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>
                          Aucune donnée sur cette période
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}

      {/* Par utilisateur */}
      {activeTab === 'users' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#333' }}>
            Top utilisateurs
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={thStyle}>Utilisateur</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Appels</th>
                  <th style={thStyle}>Coût</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((user, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          backgroundColor: '#e9ecef',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#666'
                        }}>
                          {(user.userName || '?')[0].toUpperCase()}
                        </div>
                        {user.userName || 'Inconnu'}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, color: '#666', fontSize: '13px' }}>{user.email || '-'}</td>
                    <td style={tdStyle}>{user.totalCalls}</td>
                    <td style={{ ...tdStyle, fontWeight: '600', color: '#28a745' }}>
                      {formatCurrency(user.totalCost)}
                    </td>
                  </tr>
                ))}
                {topUsers.length === 0 && (
                  <tr>
                    <td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>
                      Aucune donnée sur cette période
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Appels récents */}
      {activeTab === 'recent' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#333' }}>
            Derniers appels
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Service</th>
                  <th style={thStyle}>Opération</th>
                  <th style={thStyle}>Utilisateur</th>
                  <th style={thStyle}>Statut</th>
                  <th style={thStyle}>Durée</th>
                  <th style={thStyle}>Tokens</th>
                  <th style={thStyle}>Coût</th>
                </tr>
              </thead>
              <tbody>
                {recentCalls.map((call, idx) => {
                  const serviceInfo = serviceLabels[call.service] || { name: call.service, icon: '⚡' };
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={tdStyle}>
                        {new Date(call.createdAt).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ marginRight: '4px' }}>{serviceInfo.icon}</span>
                        {serviceInfo.name}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>
                        {call.operation}
                      </td>
                      <td style={tdStyle}>{call.user || '-'}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          backgroundColor: call.success ? '#d4edda' : '#f8d7da',
                          color: call.success ? '#155724' : '#721c24'
                        }}>
                          {call.success ? 'OK' : 'Erreur'}
                        </span>
                      </td>
                      <td style={tdStyle}>{call.durationMs ? `${call.durationMs}ms` : '-'}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>
                        {call.responseMetadata?.promptTokens ? (
                          <span title={`Input: ${call.responseMetadata.promptTokens} | Output: ${call.responseMetadata.completionTokens}`}>
                            {call.responseMetadata.promptTokens}+{call.responseMetadata.completionTokens}
                            <span style={{ color: '#999', marginLeft: '2px' }}>
                              ={call.responseMetadata.totalTokens || (call.responseMetadata.promptTokens + call.responseMetadata.completionTokens)}
                            </span>
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: '600', color: '#28a745' }}>
                        {call.costEstimate ? formatCurrency(call.costEstimate) : '-'}
                      </td>
                    </tr>
                  );
                })}
                {recentCalls.length === 0 && (
                  <tr>
                    <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>
                      Aucun appel récent
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '600',
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tdStyle = {
  padding: '12px',
  verticalAlign: 'middle'
};

export default CloudUsagePanel;
